import {
  FundingOption,
  PinwheelExperienceInput,
  PinwheelFlowPageResultTypenames,
} from '@varo/graphql-schema-types';
import { useLocalSearchParams, usePathname, useRouter } from 'expo-router';
import { fundingCallbackAtom } from 'features/onboarding/state/fundingCallbackAtom';
import { useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';
import {
  DD_EXPERIMENT_ENABLED_PAGES,
  PROGRESS_TRACKER_FUNDING,
} from 'shared/constants/onboarding';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import { useDirectDepositGateway } from 'shared/hooks/pinwheel/useDirectDepositGateway/useDirectDepositGateway';
import { useDirectDepositLoadingComponent } from 'shared/hooks/pinwheel/useDirectDepositLoadingComponent/useDirectDepositLoadingComponent';
import useDirectDepositManagementFlow from 'shared/hooks/pinwheel/useDirectDepositManagementFlow/useDirectDepositManagementFlow';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import { PinwheelOutcome } from 'shared/providers/pinwheel/PinwheelContextProvider/PinwheelContextProvider';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

type PinwheelGenericCallback = (outcome?: PinwheelOutcome) => void;
type DirectDepositIntroProps = {
  sourceName?: string;
  abCohort?: string;
};

const useFundingNavigation = (options?: { deferDdFlowInit?: boolean }) => {
  const router = useRouter();
  const pathname = usePathname();
  const { id: flowStepId } = useLocalSearchParams<{ id?: string }>();
  const deferDdFlowInit = Boolean(options?.deferDdFlowInit);
  const setCallbackMap = useSetAtom(fundingCallbackAtom);
  const { skipIntroScreen } = useDirectDepositLoadingComponent();
  const source = pathname?.includes('progress-tracker-funding')
    ? 'progress-tracker-funding'
    : undefined;

  const storeOutcomeCallback = useCallback(
    (flow: FundingOption, outcomeCallback: PinwheelGenericCallback) => {
      setCallbackMap((currentMap) => ({
        ...currentMap,
        [flow]: (outcome: { success: boolean }) =>
          outcomeCallback({ success: outcome.success }),
      }));
    },
    [setCallbackMap],
  );
  const [directDepositIntroProps, setDirectDepositIntroProps] =
    useState<DirectDepositIntroProps | null>(null);
  const { directDepositGatewayFlag, abCohort } = useDirectDepositGateway();

  const { initiatePinwheel, isPinwheelLoading: pinWheelLoading } =
    usePinwheel();
  const { refreshTiles } = useRefreshHomeTiles([
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_FOR_YOU,
    HomeTileTypeIds.NEXT_BEST_ACTION_TILE,
  ]);
  const {
    shouldRedirectToDDManagementPage,
    shouldLaunchPinwheelSDK,
    refetchDdManagementFlow,
    fetchPinWheelFlowPageData,
    handleDdManagementLinkTokenFailure,
  } = useDirectDepositManagementFlow({
    pause: deferDdFlowInit,
  });

  const navigateDirectDeposit = useCallback(
    (onOutcomeCB?: PinwheelGenericCallback) => {
      const isOnboarding = pathname?.includes('onboarding');

      const isDDExperimentEnabledFlow = DD_EXPERIMENT_ENABLED_PAGES.some(
        (page) => pathname?.includes(page),
      );
      if (
        isOnboarding ||
        (shouldLaunchPinwheelSDK && !shouldRedirectToDDManagementPage)
      ) {
        const handleInitiatePinwheel = () => {
          const handlePinwheelOutcome = async (outcome: PinwheelOutcome) => {
            if (!isOnboarding && shouldLaunchPinwheelSDK) {
              refetchDdManagementFlow(outcome);
            }
            if (outcome.success && isOnboarding) {
              void fetchPinWheelFlowPageData('network-only');
            }
            if (outcome.success && !onOutcomeCB) {
              refreshTiles();
            }
            const isRetryAfterError =
              !outcome.success &&
              outcome.hasPinwheelErrorEvent &&
              outcome.hasLoginSuccess;

            if (!isRetryAfterError) {
              onOutcomeCB?.(outcome);
              return;
            }

            const result = await fetchPinWheelFlowPageData('network-only');

            if (result.isAborted) {
              return;
            }

            if (result.error) {
              logErrorWithContext('Direct deposit confirmation error:', {
                featureArea: FeatureArea.ONBOARDING,
                responsibleTeam: ResponsibleTeam.CUSTOMER_ACQUISITION,
                severity: ErrorSeverity.LOW,
                additionalContext: {
                  error: result.error.message,
                },
              });
            }

            const isDirectDepositConfirmed =
              result.data?.pinwheelFlowPage?.__typename ===
              PinwheelFlowPageResultTypenames.DdManagementPage;

            onOutcomeCB?.({ ...outcome, success: isDirectDepositConfirmed });
          };

          initiatePinwheel({
            input: {
              experience: PinwheelExperienceInput.DirectDepositSwitch,
              skipIntroScreen,
            },
            launchBehavior: skipIntroScreen
              ? { delayAfterTokenMs: 3000 }
              : undefined,
            onOutcome: (outcome: PinwheelOutcome) => {
              void handlePinwheelOutcome(outcome);
            },
            onLinkTokenError: () => {
              handleDdManagementLinkTokenFailure();
            },
          });
        };

        if (
          directDepositGatewayFlag &&
          isDDExperimentEnabledFlow &&
          shouldLaunchPinwheelSDK
        ) {
          setDirectDepositIntroProps({
            sourceName: 'onboarding_funding',
            abCohort: abCohort,
          });
          return;
        }
        handleInitiatePinwheel();
      } else {
        router.push('/move-money/direct-deposit/direct-deposit-management');
      }
    },
    [
      pathname,
      initiatePinwheel,
      refreshTiles,
      refetchDdManagementFlow,
      shouldLaunchPinwheelSDK,
      shouldRedirectToDDManagementPage,
      fetchPinWheelFlowPageData,
      handleDdManagementLinkTokenFailure,
      skipIntroScreen,
      abCohort,
      directDepositGatewayFlag,
      router,
    ],
  );

  const navigateFundingAction = useCallback(
    (actionType: FundingOption, onOutcomeCB?: PinwheelGenericCallback) => {
      const storeCallback = (flow: FundingOption) => {
        if (onOutcomeCB) {
          storeOutcomeCallback(flow, onOutcomeCB);
        }
      };

      const isFromChecklist = pathname?.includes(PROGRESS_TRACKER_FUNDING);

      switch (actionType) {
        case FundingOption.DirectDeposit:
          navigateDirectDeposit(onOutcomeCB);
          break;

        case FundingOption.DebitCard: {
          storeCallback(FundingOption.DebitCard);
          const route = isFromChecklist
            ? '/progress-tracker-funding/debit-card-funding'
            : '/onboarding/debit-card-funding';
          router.push(route);
          break;
        }

        case FundingOption.MobileCheckDeposit: {
          router.push(
            isFromChecklist
              ? '/progress-tracker-funding/check-deposit'
              : '/onboarding/check-deposit',
          );
          break;
        }
        case FundingOption.BankTransfer: {
          router.push({
            pathname: isFromChecklist
              ? '/progress-tracker-funding/transfer/transfer-money'
              : '/onboarding/transfer/transfer-money',
            params: {
              ...(flowStepId && { flowId: flowStepId }),
              ...(source && { source }),
            },
          });
          break;
        }

        case FundingOption.CashAppPaypalVenmo: {
          router.push({
            pathname: isFromChecklist
              ? '/progress-tracker-funding/p2p/debit'
              : '/onboarding/p2p/debit',
            params: {
              ...(flowStepId && { flowId: flowStepId }),
              ...(source && { source }),
            },
          });
          break;
        }

        case FundingOption.CashDeposit: {
          router.push({
            pathname: isFromChecklist
              ? '/progress-tracker-funding/deposit-cash'
              : '/onboarding/deposit-cash',
            params: {
              ...(flowStepId && { flowId: flowStepId }),
              ...(source && { source }),
            },
          });
          break;
        }

        case FundingOption.ApplePay: {
          storeCallback(FundingOption.ApplePay);
          const route = isFromChecklist
            ? '/progress-tracker-funding/apple-pay'
            : '/onboarding/apple-pay';
          router.push(route);
          break;
        }

        case FundingOption.GooglePay: {
          storeCallback(FundingOption.GooglePay);
          const route = isFromChecklist
            ? '/progress-tracker-funding/google-pay'
            : '/onboarding/google-pay';
          router.push(route);
          break;
        }
        default:
          router.push('/');
          break;
      }
    },
    [
      navigateDirectDeposit,
      pathname,
      source,
      flowStepId,
      storeOutcomeCallback,
      router,
    ],
  );
  return {
    navigateFundingAction,
    pinWheelLoading,
    directDepositIntroProps,
  };
};

export default useFundingNavigation;
